import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, PencilSimple, Trash, Eraser, ArrowRight, Sparkle, Buildings, Storefront,
} from "@phosphor-icons/react";
import { api, WEEKDAYS_SK_SHORT } from "@/lib/app";
import { useAuth } from "@/lib/auth";
import StoreFormDialog from "@/components/StoreFormDialog";
import SmartImportDialog from "@/components/SmartImportDialog";

const fetcher = (url) => api.get(url).then((r) => r.data);


/** Dedicated view for one client: `Klienti → COOP Poprad → strediská`. */
export default function KlientDetail() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const isDeveloper = user?.role === "developer";
  const [showForm, setShowForm] = useState(false);
  const [showSmart, setShowSmart] = useState(false);

  const { data: company, error: companyErr } = useSWR(`/companies/${companyId}`, fetcher);
  const { data: stores = [], isLoading } = useSWR(`/companies/${companyId}/stores`, fetcher);

  if (companyErr) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-8 text-center" data-testid="klient-not-found">
        <Buildings size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
        <h1 className="font-display text-2xl font-bold">Klient neexistuje</h1>
        <p className="text-[var(--text-secondary)] mt-2">
          {companyErr.response?.data?.detail || "Nemáš prístup k tomuto klientovi."}
        </p>
        <Link to="/" className="btn-primary mt-6 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Späť na klientov
        </Link>
      </div>
    );
  }

  const handleDelete = async (id, name) => {
    if (!confirm(`Zmazať stredisko "${name}"? Toto zmaže aj všetky jeho dáta.`)) return;
    try {
      await api.delete(`/stores/${id}`);
      toast.success("Stredisko zmazané");
      mutate(`/companies/${companyId}/stores`);
      mutate("/stores");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodarilo sa zmazať");
    }
  };
  const handleClearData = async (id, name) => {
    if (!confirm(`Vymazať všetky dáta strediska "${name}"? Config zostane.`)) return;
    try {
      await api.delete(`/stores/${id}/data`);
      toast.success("Dáta vymazané");
      mutate(`/companies/${companyId}/stores`);
      mutate("/stores");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nepodarilo sa vymazať");
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      {/* Breadcrumb + heading */}
      <div className="mb-6">
        <Link
          to="/"
          className="text-xs text-[var(--text-muted)] hover:text-[var(--brand)] inline-flex items-center gap-1"
          data-testid="klient-back-link"
        >
          <ArrowLeft size={12} /> Klienti
        </Link>
        <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="label-eyebrow mb-1">Klient</p>
            <h1 className="font-display text-4xl font-bold tracking-tight" data-testid="klient-name">
              {company?.name || "…"}
            </h1>
            <p className="text-[var(--text-secondary)] mt-1 tabular text-sm">
              {company?.subdomain ? `${company.subdomain}.tido.sk` : ""}
            </p>
          </div>
          {isDeveloper && (
            <div className="flex gap-2">
              <button
                data-testid="klient-smart-import-button"
                onClick={() => setShowSmart(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Sparkle size={16} weight="fill" /> Smart Import
              </button>
              <button
                data-testid="klient-new-store-button"
                onClick={() => setShowForm(true)}
                className="btn-ghost inline-flex items-center gap-2"
              >
                <Plus size={14} /> Pridať stredisko
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading && <div className="text-[var(--text-secondary)]">Načítavam strediská…</div>}

      {!isLoading && stores.length === 0 && (
        <div className="card-flat p-12 text-center" data-testid="klient-empty">
          <Storefront size={40} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <h3 className="font-display text-xl font-semibold">Zatiaľ žiadne strediská</h3>
          <p className="text-[var(--text-secondary)] mt-2 max-w-md mx-auto">
            {isDeveloper
              ? <>Pridajte prvé stredisko ručne, alebo počkaj kým klient príde z TiDo linky.</>
              : <>Klient ešte nemá pridané strediská.</>}
          </p>
          {isDeveloper && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary mt-5 inline-flex items-center gap-2"
              data-testid="klient-empty-add-btn"
            >
              <Plus size={16} weight="bold" /> Pridať prvé stredisko
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stores.map((s) => {
          const cleanName = (s.name || "").replace(/\s*-\s*\d{1,2}\.\d{1,2}\.\d{4}.*$/, "").trim() || s.name;
          return (
            <div key={s.id} data-testid={`store-card-${s.code}`} className="card-flat p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <p className="label-eyebrow mb-1">Stredisko</p>
                  <h3 className="font-display text-2xl font-bold tabular leading-tight">{s.code}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 truncate" title={cleanName}>{cleanName}</p>
                </div>
                {isDeveloper && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid={`clear-data-${s.code}`}
                      onClick={() => handleClearData(s.id, s.name)}
                      className="text-[var(--text-muted)] hover:text-[var(--brand)] transition-colors p-1"
                      aria-label="Vymazať dáta"
                      title="Vymazať všetky hodinové dáta"
                    >
                      <Eraser size={16} />
                    </button>
                    <button
                      data-testid={`delete-store-${s.code}`}
                      onClick={() => handleDelete(s.id, s.name)}
                      className="text-[var(--text-muted)] hover:text-[var(--critical)] transition-colors p-1"
                      aria-label="Zmazať stredisko"
                      title="Zmazať stredisko"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                )}
              </div>
              <div className="text-sm text-[var(--text-secondary)] mb-4">
                <div>Požadovaný výkon: <span className="tabular text-[var(--text-primary)]">{s.required_hourly_performance} €/os.hod</span></div>
                <div className="mt-1 flex gap-1 flex-wrap">
                  {WEEKDAYS_SK_SHORT.map((w, i) => {
                    const cfg = s.day_configs?.[String(i)] || {};
                    const closed = cfg.closed;
                    return (
                      <span
                        key={i}
                        className={`text-[10px] tabular px-1.5 py-0.5 rounded ${
                          closed ? "bg-[var(--bg-page)] text-[var(--text-muted)]" : "bg-[var(--success-bg)] text-[var(--success)]"
                        }`}
                        title={cfg.open_from ? `${cfg.open_from}-${cfg.open_to}` : "zatvorené"}
                      >
                        {w}
                      </span>
                    );
                  })}
                </div>
              </div>
              <Link
                to={`/strediska/${s.id}`}
                data-testid={`open-store-${s.code}`}
                className="mt-auto inline-flex items-center justify-between text-sm font-medium text-[var(--brand)] hover:text-[var(--brand-hover)] group"
              >
                Otvoriť analytiku
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          );
        })}
      </div>

      {showForm && (
        <StoreFormDialog
          defaultCompanyId={companyId}
          onClose={() => {
            setShowForm(false);
            mutate(`/companies/${companyId}/stores`);
            mutate("/stores");
          }}
        />
      )}
      {showSmart && <SmartImportDialog onClose={() => setShowSmart(false)} />}
    </div>
  );
}
